import { createAsyncThunk } from '@reduxjs/toolkit';
import { userActions } from 'user/state';

import { DeviceKind, DeviceOutcome, PermissionStatus, acquireDevices, defaultAcquireDeps } from '../core';
import { getCapabilities, getMediaDevices } from '../utils';
import { devicePermissionResolved } from './devices.slice';

import type { AppDispatch, RootState } from 'store';

/**
 * The ambient {@link PermissionStatus} an acquisition outcome lets us assert — or `undefined`
 * when the outcome carries no permission verdict. Agora track gating keys off `granted`; the
 * PermissionDeniedModal keys off `denied`/`system_denied`.
 *
 * Only the verdict-bearing outcomes map: `granted`/`denied`/`system_denied`, plus `dismissed`
 * (the prompt was closed, so permission is genuinely still `prompt`). The device-failure
 * outcomes (no_device / device_unavailable / unsupported) say nothing about *permission*, so
 * they map to `undefined` and we leave the recorded permission untouched — crucially, this
 * means a no-device/busy result from a re-acquire can never silently downgrade a held grant to
 * `prompt` and block the Agora gate.
 */
const PERMISSION_BY_OUTCOME: Partial<Record<DeviceOutcome, PermissionStatus>> = {
  [DeviceOutcome.Granted]: 'granted',
  [DeviceOutcome.PermissionDenied]: 'denied',
  [DeviceOutcome.SystemDenied]: 'system_denied',
  [DeviceOutcome.Dismissed]: 'prompt',
};

type ThunkApi = { state: RootState; dispatch: AppDispatch };

/**
 * The single entry point for acquiring devices. Requests the kinds together in one
 * `getUserMedia` (a single merged prompt), falling back to per-kind acquisition only on a
 * missing-device failure — see {@link acquireDevices}. Records the resolved permission into the
 * devices slice (verdict-bearing outcomes only) and refreshes the device list so labels become
 * visible once permission is granted.
 *
 * The returned outcomes become the thunk's `fulfilled` payload; the `devices` slice
 * stores them and manages `isAcquiring` off the thunk lifecycle (pending/fulfilled/
 * rejected), so the in-flight flag is always cleared even if this body throws.
 *
 * Must be dispatched from a user gesture for Safari (the facade calls it from the
 * relevant click handler).
 */
export const acquireDevicesThunk = createAsyncThunk<
  Partial<Record<DeviceKind, DeviceOutcome>>,
  Partial<Record<DeviceKind, boolean>>,
  ThunkApi
>('[devices] acquire', async (request, { dispatch, getState }) => {
  // Capabilities are read here, not in `core`: classification needs to know whether this engine's
  // `permissions.query` can report a denial at all (it can't on WebKit — HNS-234).
  const capabilities = getCapabilities();
  const state = getState().devices;
  // WebKit on iOS reads 'prompt' whatever the user decided, so there it says nothing.
  const awaitingAnswer =
    capabilities.reportsPermissionDenial &&
    (Object.keys(request) as DeviceKind[]).some(
      (kind) => request[kind] && state[kind === 'microphone' ? 'microphonePermission' : 'cameraPermission'] === 'prompt'
    );
  const outcomes = await acquireDevices(request, { ...defaultAcquireDeps(capabilities), awaitingAnswer });

  // The dismissal that reaches the browser's limit is the last prompt it will show: from then on it
  // rejects silently, so report the block now rather than offer a "Try again" that cannot prompt.
  const { dismissals } = getState().devices;
  (Object.keys(outcomes) as DeviceKind[]).forEach((kind) => {
    const limit = capabilities.dismissalsBeforeAutoBlock;
    if (outcomes[kind] === DeviceOutcome.Dismissed && limit > 0 && dismissals[kind] + 1 >= limit) {
      outcomes[kind] = DeviceOutcome.AutoBlocked;
    }
  });

  (Object.keys(outcomes) as DeviceKind[]).forEach((kind) => {
    const outcome = outcomes[kind];
    const status = outcome !== undefined ? PERMISSION_BY_OUTCOME[outcome] : undefined;
    // Skip when the outcome carries no permission verdict, so a device-failure result never
    // overwrites (and downgrades) a permission we already hold.
    if (status) dispatch(devicePermissionResolved({ kind, status }));
  });

  const devices = await getMediaDevices();
  dispatch(userActions.navigator.mediaDevices.devicesFound(devices));

  return outcomes;
});
